import { useEffect, useRef } from 'react';
import axios from 'axios';

const ENGAGED_THRESHOLD_MS = 10000; // 10 seconds
// Use empty string for relative URLs - works in both dev and production
const API_URL = '';

/**
 * Hook to track visitor engagement.
 * Fires after user has been on page for 10+ seconds.
 */
export const useVisitorTracking = () => {
  const trackedRef = useRef(false);
  const startTimeRef = useRef(null);

  useEffect(() => {
    // Initialize start time inside useEffect to avoid impure render
    if (startTimeRef.current === null) {
      startTimeRef.current = Date.now();
    }
    
    // Generate or retrieve session ID
    let sessionId = sessionStorage.getItem('visitor_session_id');
    if (!sessionId) {
      sessionId = `vs_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('visitor_session_id', sessionId);
    }

    const trackVisit = async () => {
      if (trackedRef.current) return;
      
      const timeOnPage = Math.round((Date.now() - startTimeRef.current) / 1000);
      
      try {
        await axios.post(`${API_URL}/api/analytics/track-visit`, {
          page_url: window.location.pathname,
          time_on_page: timeOnPage,
          session_id: sessionId
        });
        trackedRef.current = true;
      } catch (err) {
        // Silent fail - analytics shouldn't break user experience
        console.debug('Visitor tracking failed:', err.message);
      }
    };

    // Track after threshold time
    const timer = setTimeout(trackVisit, ENGAGED_THRESHOLD_MS);

    // Also track on page unload if engaged
    const handleUnload = () => {
      const timeOnPage = Date.now() - startTimeRef.current;
      if (timeOnPage >= ENGAGED_THRESHOLD_MS && !trackedRef.current) {
        // Use sendBeacon for reliable unload tracking
        const data = JSON.stringify({
          page_url: window.location.pathname,
          time_on_page: Math.round(timeOnPage / 1000),
          session_id: sessionId
        });
        navigator.sendBeacon(`${API_URL}/api/analytics/track-visit`, data);
      }
    };

    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, []);
};

export default useVisitorTracking;
